import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Plane, Users, MapPin, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { companyInfo, routes as mockRoutes, testimonials, benefits } from '../data/mock';
import { api } from '../utils/apiClient';
import { useLanguage } from '../i18n/LanguageContext';
import { 
  FadeInWhenVisible, 
  RevealText, 
  StaggerContainer, 
  StaggerItem,
  CinematicCard 
} from '../components/CinematicEffects';
import { motion } from 'framer-motion';

const Home = () => {
  const { t } = useLanguage();
  const [routes, setRoutes] = useState(mockRoutes);
  
  // Cargar precios reales de la API
  useEffect(() => {
    const fetchRealPrices = async () => {
      try {
        // Obtener una fecha futura para consultar
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dateStr = tomorrow.toISOString().split('T')[0];
        
        // Consultar precios reales para cada ruta
        const updatedRoutes = await Promise.all(
          mockRoutes.map(async (route) => {
            try {
              const response = await api.get('/api/flights/search', {
                params: {
                  origin: route.origin,
                  destination: route.destination,
                  date: dateStr,
                  passengers: 1
                }
              });
              
              // Si hay vuelos, usar el precio real
              if (response.flights && response.flights.length > 0) {
                return { ...route, price: response.flights[0].price };
              }
              return route;
            } catch (error) {
              console.error(`Error fetching price for ${route.origin}-${route.destination}:`, error);
              return route;
            }
          })
        );
        
        setRoutes(updatedRoutes);
      } catch (error) {
        console.error('Error loading real prices:', error);
      }
    };
    
    fetchRealPrices();
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        {/* Background Image with Overlay and Ken Burns Effect */}
        <div className="absolute inset-0 z-0">
          <div 
            className="ken-burns absolute inset-0"
            style={{
              backgroundImage: 'url(/images/aircraft2.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute inset-0 cinematic-overlay"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8 border border-white/20"
            >
              <Star className="w-5 h-5 text-amber-400 fill-current" />
              <span className="text-white text-sm font-medium">
                Calificación {companyInfo.rating}/5 · {companyInfo.reviewCount} reseñas
              </span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            >
              {t('hero.title')}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed"
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
            >
              <Link to="/rutas">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8 py-6 shadow-2xl">
                    {t('hero.searchButton')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              </Link>
              <Link to="/contacto">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6">
                    {t('hero.contactButton')}
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const icons = {
                  star: Star,
                  plane: Plane,
                  users: Users,
                  'map-pin': MapPin
                };
                const Icon = icons[benefit.icon];

                return (
                  <div key={benefit.id} className="text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Rutas Destacadas */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('routes.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('search.searching') === 'Searching...' ? 'We connect the most important destinations in Oaxaca with daily flights' : 'Conectamos los destinos más importantes de Oaxaca con vuelos diarios'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {routes.map((route) => (
              <Card key={route.id} className="overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105 border-0">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-3 h-3 bg-blue-900 rounded-full"></div>
                        <span className="text-xl font-bold text-gray-900">{route.origin}</span>
                      </div>
                      <div className="border-l-2 border-dashed border-gray-300 h-8 ml-1.5"></div>
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                        <span className="text-xl font-bold text-gray-900">{route.destination}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-900">
                        ${route.price.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">MXN</div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6">{route.description}</p>

                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <div className="flex space-x-6 text-sm">
                      <div>
                        <span className="text-gray-500">{t('results.duration')}:</span>
                        <span className="ml-2 font-semibold text-gray-900">{route.duration}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('search.searching') === 'Searching...' ? 'Frequency' : 'Frecuencia'}:</span>
                        <span className="ml-2 font-semibold text-gray-900">{route.frequency}</span>
                      </div>
                    </div>
                    <Link to="/rutas">
                      <Button size="sm" className="bg-blue-900 hover:bg-blue-800 text-white">
                        {t('results.bookButton')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/rutas">
              <Button size="lg" variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white">
                Ver Todas las Rutas
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Descubre Nuestros Destinos */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="text-center mb-16">
              <RevealText>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  {t('destinations.title')}
                </h2>
              </RevealText>
              <motion.p 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-xl text-gray-600"
              >
                {t('destinations.subtitle')}
              </motion.p>
            </div>
          </FadeInWhenVisible>

          <StaggerContainer staggerDelay={0.2}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Puerto Escondido */}
              <StaggerItem>
                <Link to="/destinos/puerto-escondido" className="group block">
                  <CinematicCard className="overflow-hidden border-0 shadow-xl h-full">
                    <div className="relative h-64 overflow-hidden image-zoom-container">
                      <div 
                        className="image-zoom absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1582053727234-c29583712a75?w=800&q=80)' }}
                      >
                        <div className="absolute inset-0 cinematic-overlay"></div>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full mb-2"
                        >
                          PXM
                        </motion.div>
                        <h3 className="text-2xl font-bold text-white mb-1">Puerto Escondido</h3>
                        <p className="text-sm text-blue-100">
                          {t('destinations.puertoEscondido.tagline')}
                        </p>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {t('destinations.puertoEscondido.description')}
                      </p>
                      <div className="flex items-center text-blue-900 font-semibold group-hover:translate-x-2 transition-transform">
                        {t('destinations.explore')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </div>
                    </CardContent>
                  </CinematicCard>
                </Link>
              </StaggerItem>

              {/* Huatulco */}
              <StaggerItem>
                <Link to="/destinos/huatulco" className="group block">
                  <CinematicCard className="overflow-hidden border-0 shadow-xl h-full">
                    <div className="relative h-64 overflow-hidden image-zoom-container">
                      <div 
                        className="image-zoom absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1771636001068-e90df5c47b93?w=800&q=80)' }}
                      >
                        <div className="absolute inset-0 cinematic-overlay"></div>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full mb-2"
                        >
                          HUX
                        </motion.div>
                        <h3 className="text-2xl font-bold text-white mb-1">Huatulco</h3>
                        <p className="text-sm text-blue-100">
                          {t('destinations.huatulco.tagline')}
                        </p>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {t('destinations.huatulco.description')}
                      </p>
                      <div className="flex items-center text-blue-900 font-semibold group-hover:translate-x-2 transition-transform">
                        {t('destinations.explore')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </div>
                    </CardContent>
                  </CinematicCard>
                </Link>
              </StaggerItem>

              {/* Oaxaca */}
              <StaggerItem>
                <Link to="/destinos/oaxaca" className="group block">
                  <CinematicCard className="overflow-hidden border-0 shadow-xl h-full">
                    <div className="relative h-64 overflow-hidden image-zoom-container">
                      <div 
                        className="image-zoom absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1675523212590-cf205ac8ad71?w=800&q=80)' }}
                      >
                        <div className="absolute inset-0 cinematic-overlay"></div>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full mb-2"
                        >
                          OAX
                        </motion.div>
                        <h3 className="text-2xl font-bold text-white mb-1">Oaxaca</h3>
                        <p className="text-sm text-blue-100">
                          {t('destinations.oaxaca.tagline')}
                        </p>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {t('destinations.oaxaca.description')}
                      </p>
                      <div className="flex items-center text-blue-900 font-semibold group-hover:translate-x-2 transition-transform">
                        {t('destinations.explore')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </div>
                    </CardContent>
                  </CinematicCard>
                </Link>
              </StaggerItem>
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Por Qué Elegirnos */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInWhenVisible direction="left">
              <div>
                <RevealText>
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                    ¿Por qué elegir Aerotucán?
                  </h2>
                </RevealText>
                <motion.p 
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="text-xl text-gray-600 mb-12"
                >
                  Somos la aerolínea regional líder en Oaxaca, comprometida con tu seguridad y comodidad.
                </motion.p>

                <StaggerContainer staggerDelay={0.15}>
                  <div className="space-y-6">
                    {[
                      {
                        title: 'Seguridad Certificada',
                        description: 'Cumplimos con todas las normas de aviación civil y mantenimiento preventivo constante'
                      },
                      {
                        title: 'Puntualidad Garantizada',
                        description: '95% de nuestros vuelos llegan a tiempo. Tu tiempo es valioso para nosotros'
                      },
                      {
                        title: 'Servicio Personalizado',
                        description: 'Atención cercana y flexible en cada vuelo. Te conocemos por tu nombre'
                      },
                      {
                        title: 'Experiencia Local',
                        description: 'Más de 15 años conectando las comunidades y destinos de Oaxaca'
                      }
                    ].map((item, index) => (
                      <StaggerItem key={index}>
                        <motion.div 
                          className="flex items-start space-x-4"
                          whileHover={{ x: 10 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="flex-shrink-0">
                            <CheckCircle2 className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                            <p className="text-gray-600">{item.description}</p>
                          </div>
                        </motion.div>
                      </StaggerItem>
                    ))}
                  </div>
                </StaggerContainer>
              </div>
            </FadeInWhenVisible>

            <FadeInWhenVisible direction="right" delay={0.2}>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl image-zoom-container">
                  <img
                    src="https://customer-assets.emergentagent.com/job_oaxaca-airways-dev/artifacts/ul42o4ad_Screenshot%202026-03-13%20at%2012.59.12%E2%80%AFPM.png"
                    alt="Aerotucán Aircraft"
                    className="image-zoom w-full h-full object-cover"
                  />
                </div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="absolute -bottom-8 -left-8 bg-gradient-to-br from-amber-500 to-amber-600 text-white p-8 rounded-2xl shadow-2xl max-w-xs hover-lift"
                >
                  <div className="text-5xl font-bold mb-2">{companyInfo.rating}</div>
                  <div className="flex mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" />
                    ))}
                  </div>
                  <div className="text-sm opacity-90">{companyInfo.reviewCount} reseñas verificadas</div>
                </motion.div>
              </div>
            </FadeInWhenVisible>
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Lo Que Dicen Nuestros Pasajeros
            </h2>
            <p className="text-xl text-gray-600">
              La satisfacción de nuestros clientes es nuestra mejor publicidad
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-xl hover:shadow-2xl transition-all">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.comment}"</p>
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">{testimonial.location}</div>
                    </div>
                    <div className="text-xs text-gray-400">{testimonial.date}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1644896961850-9cff25bd3e0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxPYXhhY2ElMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzczMjU2NzYwfDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ¿Listo para tu próximo vuelo?
          </h2>
          <p className="text-xl text-blue-100 mb-12">
            Reserva ahora y descubre la comodidad de volar con Aerotucán
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/rutas">
              <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-6 shadow-2xl transform hover:scale-105 transition-all">
                Buscar Vuelos Ahora
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-10 py-6 transition-all">
                Llamar: {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
